import React, { useState } from "react";
import { ArrowLeft, Trash2, Edit, Send, X } from "lucide-react";
import { Message } from "../types/AdminMessages";
import img from "../imgs/chatImage.png";
import { formatDistanceToNow } from "date-fns";
import { ar } from "date-fns/locale";
import axios from "axios";
import { Config } from "../API/config";
import toast from "react-hot-toast";

const AdminMessageCard = ({ message, update }: { message: Message; update: () => void }) => {
    const [isMessageOpen, setIsMessageOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editedContent, setEditedContent] = useState(message.content);

    const acceptMessages = async () => {
        try {
            let { data } = await axios.post(`${Config.acceptMessages}/${message._id}`, {}, { withCredentials: true });
            if (data.statusCode === 201) {
                toast.success("تم قبول الرسالة");
                update();
            }
        } catch (err) {
            console.error("Error accepting message:", err);
        }
    };

    const rejectMessages = async () => {
        try {
            let { data } = await axios.post(`${Config.rejectMessages}/${message._id}`, {}, { withCredentials: true });
            if (data.statusCode === 201) {
                toast.success("تم رفض الرسالة");
                update();
            }
        } catch (err) {
            console.error("Error rejecting message:", err);
        }
    };

    const updateMessage = async () => {
        try {
            let { data } = await axios.post(`${Config.editMessages}/${message._id}`, { content: editedContent }, { withCredentials: true });
            if (data.statusCode === 201) {
                toast.success("تم تعديل الرسالة");
                update();
            }
        } catch (err) {
            console.error("Error rejecting message:", err);
        } finally {
            setIsEditOpen(false)
        }
    };

    return (
        <>
            {/* Message Card */}
            <div className="flex flex-col md:flex-row-reverse items-center bg-white border rounded-xl p-4 w-full max-w-[588px] h-auto md:h-28 text-right">
                {/* Right Section (User Avatars & Message Preview) */}
                <div className="flex flex-col md:flex-row-reverse items-center space-y-3 md:space-x-3 md:space-y-0 w-full md:w-auto">
                    <div className="flex items-center">
                        {/* Sender */}
                        <div className="flex flex-col items-center">
                            <img
                                src={message.senderId.profileImage || img}
                                alt={message.senderId.name}
                                className="w-10 h-10 rounded-full border border-gray-300"
                            />
                            <span className="text-xs text-gray-700 font-medium">{message.senderId.name}</span>
                        </div>

                        <ArrowLeft className="mx-2 text-gray-500 hidden md:block" size={16} />

                        {/* Receiver */}
                        <div className="flex flex-col items-center">
                            <img
                                src={message.receiverId.profileImage || img}
                                alt={message.receiverId.name}
                                className="w-10 h-10 rounded-full border border-gray-300"
                            />
                            <span className="text-xs text-gray-700 font-medium">{message.receiverId.name}</span>
                        </div>
                    </div>

                    <p className="text-gray-500 text-sm truncate w-40 px-4">{message.content}</p>
                </div>

                {/* Middle Section (Message Details) */}
                <div className="text-gray-700 text-sm text-center md:text-right w-full md:w-auto">
                    <p className="text-gray-500">{formatDistanceToNow(new Date(message.createdAt), { addSuffix: true, locale: ar })}</p>
                    <button
                        onClick={() => setIsMessageOpen(true)}
                        className="text-blue-500 font-medium hover:underline"
                    >
                        إقرأ الرسالة الكاملة
                    </button>
                </div>

                {/* Left Section (Action Buttons) */}
                <div className="flex flex-col md:flex-row items-center space-y-2 md:space-x-2 md:space-y-0 w-full md:w-auto">
                    <button onClick={acceptMessages} className="bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center space-x-1 w-full md:w-auto justify-center">
                        <Send size={16} />
                        <span>ابعت الرسالة</span>
                    </button>
                    <div className="flex space-x-2">
                        <Edit
                            className="text-gray-500 cursor-pointer hover:text-blue-500"
                            size={20}
                            onClick={() => setIsEditOpen(true)}
                        />
                        <Trash2
                            className="text-gray-500 cursor-pointer hover:text-red-500"
                            size={20}
                            onClick={rejectMessages}
                        />
                    </div>
                </div>
            </div>

            {/* Full Message Modal */}
            {isMessageOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setIsMessageOpen(false)}
                >
                    <div
                        className="bg-white p-6 rounded-lg shadow-lg max-w-md md:max-w-lg relative max-h-[90vh] overflow-y-auto w-1/2 md:w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setIsMessageOpen(false)}
                            className="absolute top-3 right-3 text-gray-500 hover:text-red-500"
                        >
                            <X size={20} />
                        </button>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">الرسالة الكاملة</h3>
                        <p className="text-gray-700 whitespace-pre-wrap break-words">{message.content}</p>
                    </div>
                </div>
            )}

            {/* Edit Message Modal */}
            {isEditOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setIsEditOpen(false)}
                >
                    <div
                        className="bg-white p-6 rounded-lg shadow-lg max-w-md md:max-w-lg relative w-1/2 md:w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setIsEditOpen(false)}
                            className="absolute top-3 right-3 text-gray-500 hover:text-red-500"
                        >
                            <X size={20} />
                        </button>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">تعديل الرسالة</h3>
                        <input
                            type="text"
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            className="w-full border rounded-lg p-2 mt-2"
                        />
                        <button
                            onClick={updateMessage}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg mt-4 w-full"
                        >
                            حفظ التعديل
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminMessageCard;
